import { useEffect, useRef } from 'react';
import {
  generatePath,
  NavLink,
  Outlet,
  useMatch,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import { useAppSelector } from '@store';
import {
  IconArrowLeft,
  IconLoader,
  PermissionDenied,
  PermissionEnum,
  Tabs,
} from '@ui/src';
import { OrganizationType } from '../../common/types';
import useInformation from './hooks/use-information';
import useGetOrganizationTabs from './hooks/use-get-organization-tabs';
import CustomerRightSide from '../../../components/common/info-right-side/info-right-side';
import IndividualCustomerRightSideContent from '../../../components/individual/right-side-content';

import ROUTES from '../../../routes';

import cn from './styles.module.css';
import commonStyles from '../../common/common.module.css';

const OrganizationInformation = () => {
  const navigate = useNavigate();

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const { customerId } = useParams();
  const match = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_PAGE);

  const tabsWrapperRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  const { baseInformation, totalBalance, mainChat, activeLock } =
    useAppSelector((state) => state.individualCustomers.info.general);
  const { cardsEnabled } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { activePermissions } = useAppSelector((state) => state.root);
  const { aml } = useAppSelector((state) => state.customerAml);

  const { isLoading, isFetching, notFound, query, onClear } = useInformation({
    customerId: customerId as string,
  });

  const { tabs, hiddenRoutes, withOverflow } = useGetOrganizationTabs({
    customerId,
    cardsEnabled,
    activePermissions,
    baseInformation,
    isLoading,
    tabsWrapperRef,
    tabsRef,
  });

  useEffect(() => {
    if (match && customerId) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW, { customerId }),
      );
    }
  }, [match, customerId]);

  useEffect(() => {
    let title =
      baseInformation?.firstName || baseInformation?.lastName
        ? `${baseInformation?.firstName} ${baseInformation?.lastName || ''}`
        : 'Individual Customers';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation?.firstName, baseInformation?.lastName]);

  let content;

  if (isLoading || (isFetching && !baseInformation)) {
    content = (
      <div className={commonStyles.customerLoader}>
        <IconLoader />
      </div>
    );
  } else if (!isLoading && notFound) {
    content = (
      <div className={commonStyles.userNotFound}>
        <div className={commonStyles.userNotFoundTop}>
          <NavLink
            to={`${ROUTES.CUSTOMERS_INDIVIDUAL_LIST}${query || ''}`}
            className={commonStyles.backLink}
            onClick={() => onClear()}
          >
            <IconArrowLeft width={13} height={11} />
            Back
          </NavLink>
        </div>
        <div
          className={commonStyles.userNotFoundContent}
        >{`Client with identifier ${customerId} not found`}</div>
      </div>
    );
  } else if (
    !isLoading &&
    activePermissions?.includes(PermissionEnum.INDIVIDUAL_CUSTOMERS_LIST) &&
    !notFound
  ) {
    content = (
      <>
        <div className={commonStyles.customerLeft}>
          <div className={commonStyles.linkWrapper}>
            <NavLink
              to={`${ROUTES.CUSTOMERS_INDIVIDUAL_LIST}${query || ''}`}
              className={commonStyles.backLink}
              onClick={() => onClear()}
            >
              <IconArrowLeft width={13} height={11} />
              Back
            </NavLink>
          </div>
          <div
            className={`${commonStyles.customerTabsWrapper} ${
              baseInformation?.type === OrganizationType.COMPANY_OFFICIAL
                ? cn.cofTabs
                : ''
            }`}
            ref={tabsWrapperRef}
          >
            <Tabs
              tabs={tabs}
              hiddenRoutes={hiddenRoutes}
              itemClassName={commonStyles.customerTab}
              wrapperClassName={`${commonStyles.customerTabs} ${withOverflow ? commonStyles.tabsWithOverflow : ''}`}
              wrapperRef={tabsRef}
              enableRouting
            />
          </div>
          <Outlet context={{ sendMessage }} />
        </div>
        <div className={commonStyles.customerRight}>
          <CustomerRightSide
            customerId={customerId as string}
            risk={aml?.totalRiskScore}
            balance={totalBalance?.balance as number}
            tariff={baseInformation?.tariff as string}
            customerType={OrganizationType.INDIVIDUAL}
            type={baseInformation?.type}
            name={`${baseInformation?.firstName} ${baseInformation?.lastName}`}
            mainChat={mainChat}
            status={baseInformation?.status}
          >
            <IndividualCustomerRightSideContent
              firstName={baseInformation?.firstName as string}
              lastName={baseInformation?.lastName as string}
              dateOfBirth={baseInformation?.dateOfBirth as string}
              phone={baseInformation?.phone as string}
              email={baseInformation?.email as string}
              type={baseInformation?.type}
              activeLock={activeLock}
              customerId={customerId}
              totpEnabled={baseInformation?.totpEnabled}
            />
          </CustomerRightSide>
        </div>
      </>
    );
  } else {
    content = <PermissionDenied />;
  }

  return <div className={commonStyles.customerPageContent}>{content}</div>;
};

export default OrganizationInformation;
